import { useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import { DEV_URL, rpost } from "../../utils/requests";
import "./AddHURequestModal.css";
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import FormInput from "components/FormInput/index";
import Select from "components/UnifiedSelect";
import Table from "components/UnifiedMarkupTable";

interface AddHURequestModalProps {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface IUtilityOption {
  utilityID: number;
  utilityName: string;
  enerexUtilityID: string;
}

interface IAddEntry {
  customerName: string;
  utilityID: number;
  utilityName: string;
  enerexUtilityID: string;
  commodityID: number;
  commodityName: string;
  accountNo: string;
}

interface IGroupedRequest {
  customerName: string;
  utilityId: number;
  utilityName: string;
  commodityId: number;
  commodityName: string;
  accounts: {
    accountNo: string;
    enerexUtilityId: string;
  }[];
}

const utilityOptions: IUtilityOption[] = [
  {
    utilityID: 1,
    utilityName: "CenterPoint",
    enerexUtilityID: "FABAECC2-A7BD-46F0-B883-9BA1056AF964",
  },
  {
    utilityID: 2,
    utilityName: "PEPCO DC",
    enerexUtilityID: "00C55331-CFAC-4AF7-9E76-4BCECDB58B5F",
  },
  {
    utilityID: 3,
    utilityName: "CenterPoint OH",
    enerexUtilityID: "00FB25ED-ADFA-44EF-915A-D70B0FEE21F5",
  },
];

const utilitySelectOptions = [
  { value: "0", label: "Select utility" },
  ...utilityOptions.map((utility) => ({
    value: String(utility.utilityID),
    label: utility.utilityName,
  })),
];

const findOptionByValue = (options: { value: string; label: string }[], value: number | string) =>
  options.find((option) => String(option.value) === String(value)) || null;

const AddHURequestModal = ({
  show,
  onClose,
  onSaved,
}: AddHURequestModalProps) => {
  const [commodityID, setCommodityID] = useState<number>(1);
  const [commodityName, setCommodityName] =
    useState<string>("Electric");

  const [companyID] = useState<number>(1);
  const [entries, setEntries] = useState<IAddEntry[]>([]);

  const [customerName, setCustomerName] = useState<string>("");
  const [utilityID, setUtilityID] = useState<number>(0);
  const [utilityName, setUtilityName] = useState<string>("");
  const [enerexUtilityID, setEnerexUtilityID] =
    useState<string>("");
  const [accountNos, setAccountNos] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] =
    useState<string>("");

  const huType = "Scrapper";

  const accountList = useMemo(() => {
    return accountNos
      .split(/\r?\n|,/)
      .map((account) => account.trim())
      .filter(Boolean);
  }, [accountNos]);

  const uniqueAccountList = useMemo(() => {
    return Array.from(
      new Map(
        accountList.map((account) => [
          account.toLowerCase(),
          account,
        ])
      ).values()
    );
  }, [accountList]);

  const lineNumbers = useMemo(() => {
    const lines = accountNos.split(/\r?\n/);
    return lines.length > 0 ? lines : [""];
  }, [accountNos]);

  const duplicateInputCount =
    accountList.length - uniqueAccountList.length;

  const canAdd = Boolean(
    customerName.trim() &&
      utilityID > 0 &&
      uniqueAccountList.length > 0
  );

  const resetEntryFields = () => {
    setCustomerName("");
    setUtilityID(0);
    setUtilityName("");
    setEnerexUtilityID("");
    setAccountNos("");
    setErrorMessage("");
  };

  const resetForm = () => {
    setCommodityID(1);
    setCommodityName("Electric");
    setEntries([]);
    setCustomerName("");
    setUtilityID(0);
    setUtilityName("");
    setEnerexUtilityID("");
    setAccountNos("");
    setLoading(false);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    resetForm();
    onClose();
  };

  const handleCommodityChange = (value: number) => {
    setCommodityID(value);
    setCommodityName(value === 1 ? "Electric" : "Gas");
    setErrorMessage("");
  };

  const handleUtilityChange = (value: string) => {
    const selectedUtility = utilityOptions.find(
      (utility) => utility.utilityID === Number(value)
    );

    if (!selectedUtility) {
      setUtilityID(0);
      setUtilityName("");
      setEnerexUtilityID("");
      return;
    }

    setUtilityID(selectedUtility.utilityID);
    setUtilityName(selectedUtility.utilityName);
    setEnerexUtilityID(selectedUtility.enerexUtilityID);
    setErrorMessage("");
  };

  const handleAdd = () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!customerName.trim()) {
      setErrorMessage("Please enter the customer name.");
      return;
    }

    if (utilityID === 0) {
      setErrorMessage("Please select a utility.");
      return;
    }

    if (uniqueAccountList.length === 0) {
      setErrorMessage("Please enter at least one account number.");
      return;
    }

    const existingAccountKeys = new Set(
      entries.map(
        (entry) =>
          `${entry.customerName.trim().toLowerCase()}|${
            entry.utilityID
          }|${entry.commodityID}|${entry.accountNo
            .trim()
            .toLowerCase()}`
      )
    );

    const newEntries: IAddEntry[] = [];
    let skippedDuplicateCount = 0;

    uniqueAccountList.forEach((accountNo) => {
      const duplicateKey = `${customerName
        .trim()
        .toLowerCase()}|${utilityID}|${commodityID}|${accountNo
        .trim()
        .toLowerCase()}`;

      if (existingAccountKeys.has(duplicateKey)) {
        skippedDuplicateCount += 1;
        return;
      }

      newEntries.push({
        customerName: customerName.trim(),
        utilityID,
        utilityName,
        enerexUtilityID,
        commodityID,
        commodityName,
        accountNo: accountNo.trim(),
      });
    });

    if (newEntries.length === 0) {
      setErrorMessage(
        "All entered account numbers are already added."
      );
      return;
    }

    setEntries((previousEntries) => [
      ...previousEntries,
      ...newEntries,
    ]);

    if (skippedDuplicateCount > 0) {
      setSuccessMessage(
        `${newEntries.length} account(s) added. ${skippedDuplicateCount} duplicate account(s) skipped.`
      );
    } else {
      setSuccessMessage(
        `${newEntries.length} account(s) added successfully.`
      );
    }

    resetEntryFields();
  };

  const handleRemove = (index: number) => {
    setEntries((previousEntries) =>
      previousEntries.filter(
        (_, entryIndex) => entryIndex !== index
      )
    );

    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleClearAll = () => {
    setEntries([]);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (entries.length === 0) {
      setErrorMessage("Please add at least one entry.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const groupMap = new Map<string, IGroupedRequest>();

    entries.forEach((entry) => {
      const groupKey = [
        entry.customerName.trim().toLowerCase(),
        entry.utilityID,
        entry.commodityID,
      ].join("|");

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          customerName: entry.customerName,
          utilityId: entry.utilityID,
          utilityName: entry.utilityName,
          commodityId: entry.commodityID,
          commodityName: entry.commodityName,
          accounts: [],
        });
      }

      groupMap.get(groupKey)?.accounts.push({
        accountNo: entry.accountNo,
        enerexUtilityId: entry.enerexUtilityID,
      });
    });

    const payload = Array.from(groupMap.values()).map(
      (group) => ({
        companyId: companyID,
        customerName: group.customerName,
        utilityId: group.utilityId,
        utilityName: group.utilityName,
        commodityId: group.commodityId,
        commodityName: group.commodityName,
        huType,
        accounts: group.accounts,
      })
    );

    try {
      console.log(
        "HU request payload:",
        JSON.stringify(payload, null, 2)
      );

      const response = await rpost(
        `${DEV_URL}/api/HistoricalUsage/hu-requests`,
        payload
      );

      if (response?.status !== 200) {
        throw new Error(
          response?.data || "Failed to create the HU request."
        );
      }

      resetForm();
      onClose();
      onSaved?.();
    } catch (error) {
      console.error("Error creating HU request:", error);

      setErrorMessage(
        error instanceof Error
          ? error.message
          : "An error occurred while creating the HU request."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfirmationModal
      dialogClassName="modal-md"
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={!loading}
      className="add-hu-modal"
      title="Add HU Request"
      confirmButtonLabel={loading ? "Saving..." : `Save${entries.length > 0 ? ` (${entries.length})` : ""}`}
      isLoading={loading}
      onConfirm={() => {
        if (entries.length === 0 || loading) return;
        handleSave();
      }}
    >
      <div className="hu-modal-body">
        {errorMessage && (
          <div
            className="alert alert-danger hu-alert"
            role="alert"
          >
            <i className="uil uil-exclamation-triangle me-2" />
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div
            className="alert alert-success hu-alert"
            role="alert"
          >
            <i className="uil uil-check-circle me-2" />
            {successMessage}
          </div>
        )}

        {/* Request form and account details merged into one box */}
        <div className="hu-form-section">
          <div className="row g-3 align-items-start">
            <div className="col-lg-3">
              <Form.Label>&nbsp;</Form.Label>

              <div className="commodity-radio-group">
                <label
                  className={`commodity-radio-item ${
                    commodityID === 1
                      ? "selected electric"
                      : ""
                  }`}
                  title="Electric"
                >
                  <input
                    type="radio"
                    name="commodity"
                    checked={commodityID === 1}
                    onChange={() =>
                      handleCommodityChange(1)
                    }
                    disabled={loading}
                  />

                  <span className="commodity-radio-circle">
                    <span className="commodity-radio-dot" />
                  </span>

                  <ElectricityIcon />

                </label>

                <label
                  className={`commodity-radio-item ${
                    commodityID === 2
                      ? "selected gas"
                      : ""
                  }`}
                  title="Gas"
                >
                  <input
                    type="radio"
                    name="commodity"
                    checked={commodityID === 2}
                    onChange={() =>
                      handleCommodityChange(2)
                    }
                    disabled={loading}
                  />

                  <span className="commodity-radio-circle">
                    <span className="commodity-radio-dot" />
                  </span>

                  <GasIcon />
                </label>
              </div>
            </div>

            <div className="col-lg-4">
              <Form.Label className="required">
                Customer Name
              </Form.Label>

              <FormInput
                type="text"
                name="customerName"
                value={customerName}
                onChange={(event) => {
                  setCustomerName(event.target.value);
                  setErrorMessage("");
                }}
                placeholder="Enter customer name"
                className="hu-form-control"
                disabled={loading}
                maxLength={200}
              />
            </div>

            <div className="col-lg-5">
              <Form.Label className="required">
                Utility/LDC/EDC
              </Form.Label>

              <Select
                value={findOptionByValue(utilitySelectOptions, utilityID)}
                options={utilitySelectOptions}
                onChange={(option) =>
                  handleUtilityChange(option?.value || "0")
                }
                className="hu-form-control"
                isDisabled={loading}
                isSearchable={false}
                isClearable={false}
              />
            </div>
          </div>
           <br />
          <div className="hu-section-heading">
            <div>
              <h6>Account Details</h6>
            </div>

            <span className="account-count-badge">
              {uniqueAccountList.length}{" "}
              {uniqueAccountList.length === 1
                ? "account"
                : "accounts"}
            </span>
          </div>
         
          <div className="account-editor">
            <div className="account-line-numbers">
              {lineNumbers.map((_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>

            <textarea
              value={accountNos}
              onChange={(event) => {
                setAccountNos(event.target.value);
                setErrorMessage("");
                setSuccessMessage("");
              }}
              className="account-textarea"
              spellCheck={false}
              disabled={loading}
              placeholder="Enter one account number per line"
            />
          </div>

          <div className="account-action-row">
            <div className="account-help-text">
              {duplicateInputCount > 0 && (
                <small className="duplicate-warning">
                  {duplicateInputCount} duplicate account
                  {duplicateInputCount > 1 ? "s" : ""}{" "}
                  found.
                </small>
              )}
            </div>

            <Button
              type="button"
              variant="primary"
              onClick={handleAdd}
              disabled={!canAdd || loading}
              className="add-request-button"
            >
              <i className="uil uil-plus me-1" />
              Add Request
            </Button>
          </div>
        </div>

        <div className="hu-form-section mb-0">
          <div className="hu-section-heading">
            <div>
              <h6>Added Entries</h6>
            </div>

            <div className="entry-heading-actions">
              <span className="entry-count">
                {entries.length}{" "}
                {entries.length === 1
                  ? "entry"
                  : "entries"}
              </span>

              {entries.length > 0 && (
                <button
                  type="button"
                  className="clear-all-button"
                  onClick={handleClearAll}
                  disabled={loading}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {entries.length > 0 ? (
            <div className="added-entry-table">
              <Table
                responsive
                hover
                className="mb-0 align-middle"
              >
                <thead>
                  <tr>
                    <th style={{ width: "55px" }}>#</th>
                    <th>Customer</th>
                    <th>Utility</th>
                    <th>Commodity</th>
                    <th>Account Number</th>
                    <th style={{ width: "70px" }} />
                  </tr>
                </thead>

                <tbody>
                  {entries.map((entry, index) => (
                    <tr
                      key={`${entry.customerName}-${entry.utilityID}-${entry.commodityID}-${entry.accountNo}-${index}`}
                    >
                      <td className="table-number">
                        {index + 1}
                      </td>

                      <td>
                        <div className="entry-primary-text">
                          {entry.customerName}
                        </div>
                      </td>

                      <td>
                        <div className="entry-primary-text">
                          {entry.utilityName}
                        </div>
                      </td>

                      <td>
                        <span
                          className={`commodity-badge ${
                            entry.commodityID === 1
                              ? "electric"
                              : "gas"
                          }`}
                        >
                          <i
                            className={
                              entry.commodityID === 1
                                ? "mdi mdi-lightning-bolt"
                                : "mdi mdi-fire"
                            }
                          />

                          {entry.commodityName}
                        </span>
                      </td>

                      <td>
                        <code className="account-number">
                          {entry.accountNo}
                        </code>
                      </td>

                      <td className="text-center">
                        <Button
                          type="button"
                          variant="link"
                          className="remove-entry-button"
                          onClick={() =>
                            handleRemove(index)
                          }
                          title="Remove account"
                          disabled={loading}
                        >
                          <i className="uil uil-trash-alt" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="hu-empty-state">
              <div className="hu-empty-icon">
                <i className="uil uil-clipboard-notes" />
              </div>

              <h6>No requests added yet</h6>

              <p>
                Complete the request details, enter account
                numbers, and click Add Request.
              </p>
            </div>
          )}
        </div>
        <div className="footer-summary">
          {entries.length > 0 && (
            <>
              <strong>{entries.length}</strong>

              <span>
                {entries.length === 1
                  ? "account ready"
                  : "accounts ready"}
              </span>
            </>
          )}
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default AddHURequestModal;
